import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";

export type UserRole = "superadmin" | "admin" | "member";

export const useUserRole = () => {
  return useQuery({
    queryKey: ["user-role"],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase.from("user_roles").select("role").eq("user_id", user.id).single();

      if (error) {
        // User might not have a role yet, default to member
        return "member" as UserRole;
      }

      return data?.role as UserRole;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useIsAdmin = () => {
  const { data: role, isLoading } = useUserRole();
  return {
    // Matches the server's isAdmin (api/lib/authRoutes.ts): a superadmin is
    // an admin plus more, never less.
    isAdmin: role === "admin" || role === "superadmin",
    isLoading,
  };
};

/**
 * Superadmin only, for the surfaces where admin is deliberately not enough:
 * creating teams and managing their rosters. Kept separate from useIsAdmin
 * rather than folded in, because "admin plus more" is exactly the assumption
 * that would make a team-creation button appear for admins.
 */
export const useIsSuperadmin = () => {
  const { data: role, isLoading } = useUserRole();
  return { isSuperadmin: role === "superadmin", isLoading };
};
